import React, { useState, useEffect, useRef, useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Alert } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiPlus, mdiDrag } from "@mdi/js";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { reorderItems } from "../../../utils/dndHelpers";
import { saveTemplateSections, clearSaveStatus } from "../slice";
import EditableSectionCard from "./EditableSectionCard";
import DeleteSectionModal from "./DeleteSectionModal";
import { generateId } from "../../../utils/helpers";
import "./editSectionsModal.css";

const EditSectionsModal = ({
  show,
  onHide,
  templateId,
  sections,
  onSuccess,
  onError,
}) => {
  const dispatch = useDispatch();
  const { saveTemplateSectionsStatus, saveTemplateSectionsError } = useSelector(
    (state) => state.templates
  );

  const [localSections, setLocalSections] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const [originalCollapseStates, setOriginalCollapseStates] = useState({});
  const [activeSection, setActiveSection] = useState(null);

  const scrollContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  // Configure sensors for touch and pointer devices
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // Long enough to distinguish from scrolling
        tolerance: 5, // Small tolerance for precision
      },
    })
  );

  // Initialize local sections when modal opens
  useEffect(() => {
    if (show && sections) {
      setLocalSections(JSON.parse(JSON.stringify(sections)));
      setHasChanges(false);
      setValidationErrors([]);
    }
  }, [show, sections]);

  // Handle scrollbar auto-hide behavior
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Add scrolling class when scrolling starts
      scrollContainer.classList.add("scrolling");

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Remove scrolling class after scrolling stops (500ms delay)
      scrollTimeoutRef.current = setTimeout(() => {
        scrollContainer.classList.remove("scrolling");
      }, 500);
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [show]);

  const handleClose = useCallback(() => {
    // Restore body scrolling in case drag was interrupted
    document.body.style.overflow = "";
    document.body.style.touchAction = "";

    setLocalSections([]);
    setHasChanges(false);
    setValidationErrors([]);
    setIsDragging(false);
    setDragOverId(null);
    setActiveSection(null);
    dispatch(clearSaveStatus());
    onHide();
  }, [dispatch, onHide]);

  // Handle save status changes
  useEffect(() => {
    if (saveTemplateSectionsStatus === "success") {
      onSuccess("Sections modified successfully.");
      // The parent component will update from Redux state, so no need to pass localSections
      handleClose();
      dispatch(clearSaveStatus());
    } else if (saveTemplateSectionsStatus === "failed") {
      onError(
        saveTemplateSectionsError ||
          "Failed to modify sections. Please try again."
      );
      dispatch(clearSaveStatus());
    }
  }, [
    saveTemplateSectionsStatus,
    saveTemplateSectionsError,
    onSuccess,
    onError,
    dispatch,
    handleClose,
  ]);

  const validateSections = () => {
    const errors = [];
    const sectionNames = new Set();

    localSections.forEach((section, sectionIndex) => {
      // Check for duplicate section names
      if (sectionNames.has(section.name.toLowerCase())) {
        errors.push(`Duplicate section name: "${section.name}"`);
      }
      sectionNames.add(section.name.toLowerCase());

      // Check for empty section name
      if (!section.name.trim()) {
        errors.push(`Section ${sectionIndex + 1} cannot have an empty name`);
      }

      // Check for duplicate subsection names within the same section
      if (section.subsections && section.subsections.length > 0) {
        const subsectionNames = new Set();
        section.subsections.forEach((subsection, subsectionIndex) => {
          if (subsectionNames.has(subsection.name.toLowerCase())) {
            errors.push(
              `Duplicate subsection name "${subsection.name}" in section "${section.name}"`
            );
          }
          subsectionNames.add(subsection.name.toLowerCase());

          // Check for empty subsection name
          if (!subsection.name.trim()) {
            errors.push(
              `Subsection ${subsectionIndex + 1} in section "${
                section.name
              }" cannot have an empty name`
            );
          }
        });
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = () => {
    if (validateSections()) {
      dispatch(saveTemplateSections({ templateId, sections: localSections }));
    }
  };

  const handleAddSection = () => {
    const newSection = {
      id: generateId(),
      name: "",
      subsections: [
        {
          id: generateId(),
          name: "Other",
          order: 1,
          isEditing: false,
        },
      ],
      order: localSections.length + 1,
      isEditing: true,
    };

    // Find the index of the "Other" section (case-insensitive)
    const otherSectionIndex = localSections.findIndex(
      (section) => section.name.toLowerCase().trim() === "other"
    );

    let newSections;
    if (otherSectionIndex !== -1) {
      // Insert new section before "Other" section
      newSections = [
        ...localSections.slice(0, otherSectionIndex),
        newSection,
        ...localSections.slice(otherSectionIndex),
      ];
    } else {
      // If "Other" section not found, add at the end as fallback
      newSections = [...localSections, newSection];
    }

    setLocalSections(newSections);
    setHasChanges(true);
  };

  const handleSectionUpdate = (sectionId, updates) => {
    setLocalSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId ? { ...section, ...updates } : section
      )
    );
    setHasChanges(true);
  };

  const handleSectionDelete = (sectionId) => {
    const section = localSections.find((s) => s.id === sectionId);

    // Check if section has subsections (excluding "Other" subsection)
    const hasNonOtherSubsections = section?.subsections?.some(
      (sub) => sub.name.toLowerCase().trim() !== "other"
    );

    if (hasNonOtherSubsections) {
      // Show confirmation modal
      setSectionToDelete(section);
      setShowDeleteModal(true);
    } else {
      // Direct delete if no subsections or only "Other" subsection
      setLocalSections((prevSections) =>
        prevSections.filter((s) => s.id !== sectionId)
      );
      setHasChanges(true);
    }
  };

  const handleConfirmDeleteSection = () => {
    if (sectionToDelete) {
      setLocalSections((prevSections) =>
        prevSections.filter((s) => s.id !== sectionToDelete.id)
      );
      setHasChanges(true);
      setSectionToDelete(null);
      setShowDeleteModal(false);
    }
  };

  const handleSubsectionUpdate = (sectionId, subsectionId, updates) => {
    setLocalSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              subsections: section.subsections.map((subsection) =>
                subsection.id === subsectionId
                  ? { ...subsection, ...updates }
                  : subsection
              ),
            }
          : section
      )
    );
    setHasChanges(true);
  };

  const handleSubsectionDelete = (sectionId, subsectionId) => {
    setLocalSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              subsections: section.subsections.filter(
                (subsection) => subsection.id !== subsectionId
              ),
            }
          : section
      )
    );
    setHasChanges(true);
  };

  const handleAddSubsection = (sectionId) => {
    const newSubsection = {
      id: generateId(),
      name: "",
      order: 1,
      isEditing: true,
    };

    setLocalSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              subsections: (() => {
                const currentSubsections = section.subsections || [];

                // Find the index of the "Other" subsection (case-insensitive)
                const otherSubsectionIndex = currentSubsections.findIndex(
                  (subsection) =>
                    subsection.name.toLowerCase().trim() === "other"
                );

                if (otherSubsectionIndex !== -1) {
                  // Insert new subsection before "Other" subsection
                  return [
                    ...currentSubsections.slice(0, otherSubsectionIndex),
                    newSubsection,
                    ...currentSubsections.slice(otherSubsectionIndex),
                  ];
                } else {
                  // If "Other" subsection not found, add at the end as fallback
                  return [...currentSubsections, newSubsection];
                }
              })(),
            }
          : section
      )
    );
    setHasChanges(true);
  };

  const handleDragStart = (event) => {
    setIsDragging(true);
    const draggedId = event.active.id;

    // Detect mobile device
    const isMobile = window.innerWidth <= 767 || "ontouchstart" in window;

    // Prevent body scrolling during drag
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    // On mobile: also prevent modal content scrolling to fix drag behavior
    if (isMobile && scrollContainerRef.current) {
      scrollContainerRef.current.classList.add("dragging");
    }

    // Find and store the active section for drag preview (always collapsed)
    const draggedSection = localSections.find(
      (section) => section.id === draggedId
    );
    setActiveSection({ ...draggedSection, collapsed: true });

    // Store original collapse states by reading current DOM state
    const collapseStates = {};
    localSections.forEach((section) => {
      const sectionElement = document.querySelector(
        `[data-section-id="${section.id}"]`
      );
      const subsectionsContainer = sectionElement?.querySelector(
        ".subsections-container"
      );
      // If subsections container exists and is visible, section is expanded (collapsed = false)
      collapseStates[section.id] =
        !subsectionsContainer || subsectionsContainer.style.display === "none";
    });
    setOriginalCollapseStates(collapseStates);

    // Collapse ALL sections immediately for consistent behavior
    setLocalSections((prevSections) =>
      prevSections.map((section) => ({
        ...section,
        collapsed: true,
      }))
    );
  };

  const handleDragOver = (event) => {
    const { over } = event;
    setDragOverId(over?.id || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    setIsDragging(false);
    setDragOverId(null);
    setActiveSection(null);

    // Restore body scrolling
    document.body.style.overflow = "";
    document.body.style.touchAction = "";

    // Restore modal content scrolling on mobile
    const isMobile = window.innerWidth <= 767 || "ontouchstart" in window;
    if (isMobile && scrollContainerRef.current) {
      scrollContainerRef.current.classList.remove("dragging");
    }

    if (active.id !== over?.id && over) {
      // Prevent dropping below "Other" section
      const overSection = localSections.find((s) => s.id === over.id);

      if (overSection?.name.toLowerCase().trim() === "other") {
        // Just restore original collapse states if drop is blocked
        setLocalSections((prevSections) =>
          prevSections.map((section) => ({
            ...section,
            collapsed: originalCollapseStates[section.id] || false,
          }))
        );
        return;
      }

      // Reorder items AND restore original collapse states in one operation
      setLocalSections((prevSections) => {
        const reorderedItems = reorderItems(prevSections, active.id, over.id);
        return reorderedItems.map((section) => ({
          ...section,
          collapsed: originalCollapseStates[section.id] || false,
        }));
      });
      setHasChanges(true);

      // Auto-scroll to the dropped section's new position after a brief delay
      setTimeout(() => {
        const droppedElement = document.querySelector(
          `[data-section-id="${active.id}"]`
        );
        if (droppedElement && scrollContainerRef.current) {
          droppedElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    } else {
      // If no valid drop target, just restore original collapse states
      setLocalSections((prevSections) =>
        prevSections.map((section) => ({
          ...section,
          collapsed: originalCollapseStates[section.id] || false,
        }))
      );
    }
  };

  const canSave = hasChanges && validationErrors.length === 0;

  return (
    <Modal
      show={show}
      onHide={() => {}}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      className="edit-sections-modal"
    >
      <Modal.Body className="pt-0" style={{ position: "relative" }}>
        {showDeleteModal && <div className="edit-sections-modal-overlay" />}
        <Modal.Header className="modal-title-with-border mb-0">
          <Modal.Title>Edit Sections</Modal.Title>
          <Button
            variant="outline-primary"
            onClick={handleAddSection}
            size="sm"
            className="ms-auto"
          >
            <Icon path={mdiPlus} size={0.8} className="me-2" />
            Add Section
          </Button>
        </Modal.Header>
        <Modal.Body className="pb-0 px-0">
          {validationErrors.length > 0 && (
            <Alert variant="danger" className="mb-3">
              <ul className="mb-0">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          <div className="edit-sections-content" ref={scrollContainerRef}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localSections.filter(
                  (section) => section.name.toLowerCase().trim() !== "other"
                )}
                strategy={verticalListSortingStrategy}
              >
                {localSections.map((section, index) => {
                  const isOtherSection =
                    section.name.toLowerCase().trim() === "other";
                  const showDropIndicator =
                    isDragging && dragOverId === section.id && !isOtherSection;

                  return (
                    <div key={section.id} data-section-id={section.id}>
                      {showDropIndicator && (
                        <div
                          className="drag-drop-indicator"
                          style={{
                            height: "3px",
                            backgroundColor: "#0D6EFD",
                            margin: "-8px 0 5px 0",
                            borderRadius: "2px",
                          }}
                        />
                      )}
                      <EditableSectionCard
                        section={section}
                        onSectionUpdate={handleSectionUpdate}
                        onSectionDelete={handleSectionDelete}
                        onSubsectionUpdate={handleSubsectionUpdate}
                        onSubsectionDelete={handleSubsectionDelete}
                        onAddSubsection={handleAddSubsection}
                        allSections={localSections}
                        allSubsections={section.subsections || []}
                        isDragDisabled={isOtherSection}
                      />
                    </div>
                  );
                })}
              </SortableContext>
              <DragOverlay>
                {activeSection ? (
                  <div
                    style={{
                      background: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "12px",
                      cursor: "grabbing",
                      maxWidth: "400px",
                      minWidth: "fit-content",
                      width: "auto",
                      zIndex: 99999,
                      position: "relative",
                    }}
                  >
                    <Icon
                      path={mdiDrag}
                      size={1}
                      className="text-muted"
                      style={{ flexShrink: 0 }}
                    />
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#212529",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {activeSection.name || "Section Name..."}
                    </span>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </Modal.Body>
        <Modal.Footer className="mt-0">
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            disabled={saveTemplateSectionsStatus === "loading"}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!canSave || saveTemplateSectionsStatus === "loading"}
          >
            {saveTemplateSectionsStatus === "loading" ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal.Body>

      {/* Delete Section Modal */}
      <DeleteSectionModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        section={sectionToDelete}
        onConfirm={handleConfirmDeleteSection}
      />
    </Modal>
  );
};

export default EditSectionsModal;
